import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  Sparkles, BookOpen, ShieldCheck, Globe2, Users, Award, MessageCircle,
  Heart, Compass, Briefcase, Hash, Home as HomeIcon, Flower2, Moon, Sun,
  Quote, ArrowRight, MapPin, Tv, Youtube, Mic
} from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import FloatingActions from '../components/FloatingActions';
import Footer from '../components/Footer';
import RelatedTools from '../components/RelatedTools';
import { useLang } from '../context/LanguageContext';
import { trackConsultationCTAClick, trackWhatsAppClick } from '../utils/analytics';

const ASTROLOGER_PHOTO = '/media/founder/2_-_Copy.jpg';
const ASTROLOGER_PHOTO_BG = '/media/founder/IMG_9620-removebg-preview.png';
const WHATSAPP_NUMBER = '919311369729';

const buildWA = (msg) => `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(msg)}`;

const onCTAClick = (location) => {
  trackConsultationCTAClick(location, { page: 'about' });
  trackWhatsAppClick(location);
};

// === SECTION SUB-COMPONENTS ===

const TrustBadge = ({ icon: Icon, label, sublabel }) => (
  <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-white/[0.03] border border-gold/15 backdrop-blur-sm">
    <div className="w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br from-amber-700/30 to-purple-900/40 border border-gold/30 flex items-center justify-center">
      <Icon className="w-5 h-5 text-gold" />
    </div>
    <div>
      <div className="text-text-main font-semibold text-sm leading-tight">{label}</div>
      {sublabel && <div className="text-text-muted text-[11px] leading-tight">{sublabel}</div>}
    </div>
  </div>
);

const ExpertiseCard = ({ icon: Icon, title, titleEn, body, accent = 'gold' }) => (
  <motion.div
    whileHover={{ y: -3 }}
    className="group relative overflow-hidden rounded-2xl border border-gold/15 bg-white/[0.025] p-5 sm:p-6 hover:border-gold/40 transition-colors"
  >
    <div className="absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-to-br from-gold/8 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
    <div className="relative">
      <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-amber-700/25 to-purple-900/40 border border-gold/30 flex items-center justify-center mb-4">
        <Icon className="w-5 h-5 text-gold" />
      </div>
      <h3 className="font-heading text-xl text-text-main mb-1">{title}</h3>
      <div className="text-[11px] uppercase tracking-[0.2em] text-gold/70 mb-3">{titleEn}</div>
      <p className="text-text-muted text-sm leading-[1.7]">{body}</p>
    </div>
  </motion.div>
);

const Shloka = ({ sanskrit, meaning, source }) => (
  <div className="relative bg-gradient-to-br from-amber-950/30 via-purple-950/30 to-amber-950/20 border border-gold/20 rounded-2xl p-5 sm:p-6">
    <Quote className="absolute top-4 right-4 w-4 h-4 text-gold/30" />
    <p className="text-gold font-heading text-base sm:text-lg leading-[1.9] italic whitespace-pre-line">{sanskrit}</p>
    {meaning && <p className="text-text-muted text-sm mt-3 leading-relaxed border-t border-gold/10 pt-3">— {meaning}</p>}
    {source && <p className="text-[11px] text-gold/60 mt-2">📿 {source}</p>}
  </div>
);

// === MAIN PAGE ===

export default function AboutPage() {
  const { lang } = useLang();
  const isHi = lang === 'hi';

  // SEO: Person + AboutPage JSON-LD
  const personSchema = {
    '@context': 'https://schema.org',
    '@type': 'Person',
    '@id': 'https://viratastro.cloud/about#person',
    name: 'Vivek Mudgal',
    alternateName: 'आचार्य विवेक मुदगल',
    jobTitle: 'Parashari Jyotish & Vastu Acharya',
    description:
      '33+ years experienced Vedic Acharya offering Parashari Jyotish, Vastu Shastra and spiritual life-guidance to seekers across India and worldwide.',
    image: ASTROLOGER_PHOTO,
    url: 'https://viratastro.cloud/about',
    worksFor: { '@type': 'Organization', '@id': 'https://viratastro.cloud/#localbusiness', name: 'ViratAstro' },
    address: { '@type': 'PostalAddress', addressLocality: 'Faridabad', addressRegion: 'Haryana', addressCountry: 'IN' },
    knowsAbout: [
      'Parashari Jyotish', 'Vedic Astrology', 'Vastu Shastra', 'Astro-Vastu',
      'Numerology', 'Graha Dasha Analysis', 'Spiritual Remedies', 'Marriage Guidance',
      'Career Direction', 'Online Vedic Consultation'
    ],
    knowsLanguage: ['Hindi', 'English'],
    award: ['33+ Years Experience', '33,000+ Kundlis Studied'],
  };
  const aboutPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: 'About Vivek Mudgal — Parashari Jyotish & Vastu Acharya',
    description:
      'Learn about Acharya Vivek Mudgal, a Parashari Jyotish and Vastu guide with 33+ years of experience offering spiritual and life guidance worldwide.',
    url: 'https://viratastro.cloud/about',
    mainEntity: { '@id': 'https://viratastro.cloud/about#person' },
    publisher: { '@id': 'https://viratastro.cloud/#localbusiness' },
  };

  return (
    <div className="min-h-screen bg-background" data-testid="about-page">
      <SEO
        title="About Vivek Mudgal | Parashari Jyotish & Vastu Guidance"
        description="Learn about Acharya Vivek Mudgal, a Parashari Jyotish and Vastu guide with 33+ years of experience offering spiritual and life guidance worldwide."
        path="/about"
        image={ASTROLOGER_PHOTO}
        breadcrumbs={[
          { name: isHi ? 'होम' : 'Home', path: '/' },
          { name: isHi ? 'परिचय' : 'About', path: '/about' },
        ]}
        keywords="Vivek Mudgal, आचार्य विवेक मुदगल, Parashari Jyotish, Vedic Astrologer Faridabad, Vastu Consultant Delhi NCR, Astro-Vastu Expert, Online Vedic Consultation, Spiritual Guidance, ViratAstro"
        jsonLd={[personSchema, aboutPageSchema]}
      />
      <Header />
      {/* === 1. HERO === */}
      <section className="relative overflow-hidden pt-24 sm:pt-28 pb-16 sm:pb-20" data-testid="about-hero">
        <div className="absolute inset-0 cosmic-bg opacity-60" />
        <div className="absolute -top-20 -right-32 w-[480px] h-[480px] rounded-full bg-gold/8 blur-[140px] pointer-events-none" />
        <div className="absolute -bottom-20 -left-32 w-[460px] h-[460px] rounded-full bg-purple/15 blur-[140px] pointer-events-none" />

        <div className="container-custom relative z-10">
          <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
            {/* Text */}
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
              <p className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gold/10 border border-gold/30 text-gold text-[11px] font-bold tracking-[0.2em] uppercase mb-5">
                <Sparkles className="w-3.5 h-3.5" /> {isHi ? 'परिचय' : 'About'}
              </p>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-text-main leading-[1.1] tracking-tight mb-3">
                {isHi ? <>आचार्य <span className="text-gold-gradient">विवेक मुदगल</span></> : <>Acharya <span className="text-gold-gradient">Vivek Mudgal</span></>}
              </h1>
              <p className="text-base sm:text-lg text-gold/90 font-heading italic mb-5">
                Parashari Jyotish • Vastu Shastra • Spiritual Guidance
              </p>
              <p className="text-text-muted text-base sm:text-[17px] leading-[1.85] max-w-xl mb-7">
                {isHi
                  ? '33+ वर्षों से वैदिक ज्योतिष, वास्तु और आध्यात्मिक मार्गदर्शन के माध्यम से लोगों को जीवन की दिशा समझाने का प्रयास।'
                  : 'For 33+ years, gently guiding seekers through Vedic Jyotish, Vastu Shastra and spiritual insight — helping each soul find clarity, direction and inner balance.'}
              </p>

              {/* Trust badges */}
              <div className="grid grid-cols-2 gap-3 max-w-xl mb-8">
                <TrustBadge icon={Award} label={isHi ? '33+ वर्षों का अनुभव' : '33+ Years Experience'} sublabel={isHi ? 'पराशर शास्त्र' : 'Parashari Tradition'} />
                <TrustBadge icon={Users} label={isHi ? '33,000+ कुंडलियाँ' : '33,000+ Kundlis Studied'} sublabel={isHi ? 'गहन अध्ययन' : 'In-depth analysis'} />
                <TrustBadge icon={Globe2} label={isHi ? 'विश्वव्यापी परामर्श' : 'Worldwide Consultation'} sublabel={isHi ? 'India • UAE • US • UK' : 'India • UAE • US • UK'} />
                <TrustBadge icon={MapPin} label="Faridabad • Delhi NCR" sublabel={isHi ? 'मुख्यालय' : 'Headquarters'} />
              </div>

              <a
                href={buildWA(isHi ? 'नमस्ते आचार्य जी, मुझे आपसे एक बार बात करनी है।' : 'Namaste Acharya ji, I would like to speak with you.')}
                target="_blank" rel="noopener noreferrer"
                onClick={() => onCTAClick('about_hero')}
                className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full bg-gradient-to-r from-gold via-amber-400 to-amber-500 text-purple-950 font-bold text-base shadow-xl hover:scale-[1.03] active:scale-[0.98] transition-transform"
                data-testid="about-hero-wa-cta">
                <MessageCircle className="w-5 h-5" />
                {isHi ? 'WhatsApp पर बात करें' : 'Talk on WhatsApp'}
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>

            {/* Photo */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.15 }} className="relative">
              <div className="relative max-w-md mx-auto">
                <div className="absolute -inset-4 bg-gradient-to-br from-gold/25 via-amber-500/15 to-purple/30 rounded-[2rem] blur-2xl" />
                <div className="relative rounded-[2rem] overflow-hidden border-2 border-gold/40 aspect-[4/5] bg-gradient-to-b from-purple-950/40 to-amber-950/20">
                  <img src={ASTROLOGER_PHOTO} alt="Acharya Vivek Mudgal" className="w-full h-full object-cover object-top" data-testid="about-hero-photo" />
                </div>
                {/* Floating sanskrit chip */}
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-5 py-2 rounded-full bg-purple-950/90 border border-gold/40 backdrop-blur shadow-lg whitespace-nowrap">
                  <span className="text-gold font-heading text-sm">🕉️ ॐ गुरवे नमः</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* === 2. HUMAN INTRODUCTION === */}
      <section className="py-16 sm:py-20 relative" data-testid="about-intro">
        <div className="container-custom max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/80 font-semibold mb-3 text-center">{isHi ? 'मेरी कहानी' : 'My Story'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-main text-center mb-3 leading-tight">
              {isHi ? 'ज्योतिष — भविष्यवाणी नहीं, मार्गदर्शन है' : 'Jyotish — not prediction, but guidance'}
            </h2>
            <div className="divine-divider mt-4 mb-10 mx-auto max-w-xs" />

            <div className="space-y-5 text-text-main text-[16px] sm:text-[17px] leading-[1.95]">
              <p className="text-text-muted">
                {isHi
                  ? 'जब कोई पहली बार मेरे पास आता है, अधिकतर एक ही बात कहता है — "मन में बहुत उलझन है।" और मैं समझता हूँ। ज़िन्दगी हर दिन कुछ नए सवाल लेकर आती है — रिश्ते, करियर, स्वास्थ्य, संतान, आर्थिक चिंता।'
                  : 'When someone walks in for the first time, most often they say one thing — "There is a lot of confusion in my mind." And I understand. Life keeps bringing new questions every day — relationships, career, health, family, financial worries.'}
              </p>
              <p>
                {isHi
                  ? 'मैंने 33 वर्षों के अनुभव में एक बात बहुत स्पष्ट देखी है — असली ज्योतिष डर नहीं देता, स्पष्टता देता है। हर कुंडली अलग होती है, हर आत्मा का कर्म-पथ अलग होता है। जब हम पराशर ऋषि की दृष्टि से कुंडली को देखते हैं — तब केवल "क्या होगा" नहीं, बल्कि "अभी मुझे क्या करना चाहिए" का उत्तर मिलता है।'
                  : 'In my 33 years of practice, one truth has become very clear — real Jyotish never creates fear, it brings clarity. Every chart is unique, every soul carries its own karmic path. When we read a chart through the lens of Maharshi Parashar, we don\'t just see "what will happen" — we discover "what should I do now."'}
              </p>
              <p className="text-text-muted">
                {isHi
                  ? 'मेरा प्रयास हमेशा यही रहा है — व्यक्ति को उसकी अपनी शक्ति का परिचय कराना। ग्रह सिर्फ संकेत हैं; निर्णय आत्मा करती है। हर बैठक एक शांत संवाद है — कोई automated report नहीं, कोई बना-बनाया formula नहीं — सिर्फ आपकी कुंडली, आपके प्रश्न, और शास्त्रीय दृष्टिकोण।'
                  : 'My intent has always remained the same — to help a person see their own inner strength. Planets are signposts; the soul makes the decisions. Every conversation is a calm dialogue — no automated reports, no copy-paste formulas — only your chart, your questions, and the wisdom of the shastras.'}
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* === 3. SHASTRIYA FOUNDATION === */}
      <section className="py-16 sm:py-20 relative bg-white/[0.015]" data-testid="about-shastra">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-gold/30 to-transparent" />
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-12">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/80 font-semibold mb-3">{isHi ? 'शास्त्रीय आधार' : 'Shastric Foundation'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-main mb-3">
              {isHi ? <>पराशर ऋषि से मिली <span className="text-gold-gradient">परंपरा</span></> : <>The <span className="text-gold-gradient">tradition</span> from Maharshi Parashar</>}
            </h2>
            <p className="text-text-muted max-w-2xl mx-auto leading-relaxed">
              {isHi
                ? 'मेरा हर विश्लेषण इन शास्त्रीय ग्रंथों के सिद्धांतों पर आधारित है — ग्रह, दशा, भाव, दृष्टि और कर्म-आधारित विवेचन।'
                : 'Every reading I offer is rooted in these classical texts — analysis through Graha, Dasha, Bhava, Drishti and Karma-based interpretation.'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 gap-5 mb-10">
            {[
              { hi: 'बृहत् पराशर होरा शास्त्र', en: 'Brihat Parashara Hora Shastra', note: isHi ? 'वैदिक ज्योतिष का मूल ग्रंथ' : 'The foundational text of Vedic Jyotish' },
              { hi: 'फलदीपिका', en: 'Phaladeepika', note: isHi ? 'फलकथन का स्तंभ' : 'Pillar of result-interpretation' },
              { hi: 'सारावली', en: 'Saravali', note: isHi ? 'राजयोग व ग्रह संयोग' : 'Raja Yogas & planetary combinations' },
              { hi: 'जातक पारिजात', en: 'Jataka Parijata', note: isHi ? 'सूक्ष्म जीवन-विश्लेषण' : 'Subtle life-analysis' },
            ].map((g) => (
              <div key={g.en} className="rounded-2xl border border-gold/20 bg-white/[0.03] p-5 sm:p-6 backdrop-blur-sm">
                <div className="flex items-start gap-3">
                  <BookOpen className="w-5 h-5 text-gold mt-1 shrink-0" />
                  <div>
                    <h3 className="font-heading text-xl text-text-main">{g.hi}</h3>
                    <p className="text-[11px] uppercase tracking-[0.18em] text-gold/70 mt-0.5">{g.en}</p>
                    <p className="text-sm text-text-muted mt-2 leading-relaxed">{g.note}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <Shloka
            sanskrit={'ज्योतिषं नयनं प्रोक्तं वेदस्य भगवत्तमैः ।\nतस्मादद्धीततत्त्वोऽयं वेदविन्न परो विदुः ॥'}
            meaning={isHi
              ? 'ज्योतिष को वेदों के नेत्र कहा गया है। जिसने इसके तत्व को समझ लिया, उसके समान वेदज्ञ कोई और नहीं।'
              : 'Jyotish has been called the eye of the Vedas — one who truly grasps its essence is unmatched in Vedic insight.'}
            source={isHi ? 'नारद पुराण' : 'Narada Purana'}
          />
        </div>
      </section>

      {/* === 4. EXPERIENCE & EXPERTISE === */}
      <section className="py-16 sm:py-20 relative" data-testid="about-expertise">
        <div className="container-custom">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/80 font-semibold mb-3">{isHi ? 'अनुभव के क्षेत्र' : 'Areas of Practice'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-main mb-3 leading-tight">
              {isHi ? <>एक ही दृष्टि — <span className="text-gold-gradient">शास्त्र और जीवन</span></> : <>One Vision — <span className="text-gold-gradient">Shastra meets Life</span></>}
            </h2>
            <p className="text-text-muted leading-relaxed">
              {isHi
                ? 'विभिन्न क्षेत्रों में, परंतु सिद्धांत एक ही — पराशरीय शास्त्र की सजीव दृष्टि।'
                : 'Different domains, one principle — the living lens of Parashari Shastra.'}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            <ExpertiseCard icon={Sparkles} title={isHi ? 'पराशरी ज्योतिष' : 'Parashari Jyotish'} titleEn="Parashari Jyotish"
              body={isHi ? 'जन्म कुंडली का गहन Parashari विश्लेषण — ग्रह, भाव, दशा, दृष्टि और शास्त्रीय योगों के माध्यम से।' : 'In-depth Parashari analysis of the natal chart — through grahas, bhavas, dashas, aspects and classical yogas.'} />
            <ExpertiseCard icon={Heart} title={isHi ? 'विवाह मार्गदर्शन' : 'Marriage Guidance'} titleEn="Marriage Guidance"
              body={isHi ? 'गुण मिलान, मांगलिक विश्लेषण और जीवनसाथी के साथ सामंजस्य पर शांत संवाद।' : 'Gun Milan, Manglik analysis and a calm dialogue on harmony with one\'s life partner.'} />
            <ExpertiseCard icon={Briefcase} title={isHi ? 'करियर दिशा' : 'Career Direction'} titleEn="Career Direction"
              body={isHi ? 'दशम भाव, कर्म-योग और दशा-काल के अनुसार करियर-निर्णय में स्पष्टता।' : 'Clarity in career decisions through 10th house, karma-yogas and dasha-periods.'} />
            <ExpertiseCard icon={HomeIcon} title={isHi ? 'एस्ट्रो-वास्तु' : 'Astro-Vastu'} titleEn="Astro-Vastu"
              body={isHi ? 'जन्म कुंडली + दिशाओं + ग्रह स्वामी का संयुक्त विश्लेषण — आपके घर के लिए विशेष।' : 'Combined analysis of birth chart, directions and planetary lords — tailored to your home.'} />
            <ExpertiseCard icon={Hash} title={isHi ? 'अंकशास्त्र' : 'Numerology'} titleEn="Numerology"
              body={isHi ? 'मूलांक, भाग्यांक और नाम-अंक का वैदिक संयोग — व्यावहारिक अनुप्रयोग के साथ।' : 'Mulank, Bhagyank and Naam-ank in Vedic synthesis — with practical guidance.'} />
            <ExpertiseCard icon={Moon} title={isHi ? 'दशा विश्लेषण' : 'Graha Dasha Analysis'} titleEn="Graha Dasha Analysis"
              body={isHi ? 'विंशोत्तरी महादशा-अंतर्दशा-प्रत्यंतर — आज के समय का सटीक संदर्भ।' : 'Vimshottari Mahadasha–Antardasha–Pratyantar — precise context for the present time.'} />
            <ExpertiseCard icon={Flower2} title={isHi ? 'आध्यात्मिक उपाय' : 'Spiritual Remedies'} titleEn="Spiritual Remedies"
              body={isHi ? 'मंत्र, दान, साधना और जीवन-शैली पर आधारित व्यावहारिक उपाय — ताबीज़ या भय नहीं।' : 'Mantra, daana, sadhana and lifestyle-based practical remedies — never fear-based or amulet-driven.'} />
            <ExpertiseCard icon={Globe2} title={isHi ? 'ऑनलाइन परामर्श' : 'Online Consultation'} titleEn="Online Consultation"
              body={isHi ? 'WhatsApp / Zoom पर शांत व्यक्तिगत संवाद — भारत और विश्व भर से।' : 'A calm personal dialogue on WhatsApp / Zoom — for seekers across India and worldwide.'} />
          </div>
        </div>
      </section>

      {/* === 5. INDIA + INTERNATIONAL === */}
      <section className="py-16 sm:py-20 relative" data-testid="about-international">
        <div className="absolute inset-0 bg-gradient-to-b from-purple-950/30 via-transparent to-amber-950/15 pointer-events-none" />
        <div className="container-custom max-w-4xl relative">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/80 font-semibold mb-3">{isHi ? 'भारत + विश्व' : 'India + Worldwide'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-main mb-3">
              {isHi ? <>दूरी अब <span className="text-gold-gradient">बाधा नहीं</span></> : <>Distance is no longer a <span className="text-gold-gradient">barrier</span></>}
            </h2>
          </div>

          <div className="rounded-2xl border border-gold/20 bg-white/[0.03] p-6 sm:p-8 backdrop-blur-sm">
            <p className="text-text-main text-[16px] sm:text-[17px] leading-[1.95] mb-5">
              {isHi
                ? 'आज digital माध्यमों के द्वारा भारत, UAE (Dubai, Abu Dhabi, Sharjah), USA, UK, Canada, Australia और विश्व के विभिन्न देशों से लोग वैदिक ज्योतिष आधारित जीवन-मार्गदर्शन प्राप्त कर रहे हैं।'
                : 'Today, through digital channels, seekers from India, the UAE (Dubai, Abu Dhabi, Sharjah), USA, UK, Canada, Australia and many other parts of the world receive Vedic insight and life-guidance.'}
            </p>
            <p className="text-text-muted text-[15px] leading-[1.85]">
              {isHi
                ? 'हर परामर्श व्यक्ति की कुंडली, उसकी संस्कृति और उसकी देश-स्थिति को ध्यान में रखकर शांत और शास्त्रीय रूप से किया जाता है। उद्देश्य कभी "भविष्य बताना" नहीं — बल्कि "वर्तमान को समझाना" है।'
                : 'Every consultation respects your chart, your cultural setting and your present life-context — guided in a calm, shastric tone. The goal is never to "predict the future," but to help you understand the present.'}
            </p>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { flag: '🇮🇳', name: 'India' },
                { flag: '🇦🇪', name: 'UAE' },
                { flag: '🇺🇸', name: 'USA' },
                { flag: '🇬🇧', name: 'UK' },
                { flag: '🇨🇦', name: 'Canada' },
                { flag: '🇦🇺', name: 'Australia' },
                { flag: '🇸🇬', name: 'Singapore' },
                { flag: '🌍', name: isHi ? 'अन्य' : 'Others' },
              ].map((c) => (
                <div key={c.name} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-white/[0.04] border border-gold/10 text-sm text-text-main">
                  <span className="text-lg">{c.flag}</span>
                  <span>{c.name}</span>
                </div>
              ))}
            </div>

            <div className="mt-6 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gold/12 border border-gold/35 text-gold text-sm font-semibold">
              <Globe2 className="w-4 h-4" /> {isHi ? 'Online Consultation Available Worldwide' : 'Online Consultation Available Worldwide'}
            </div>
          </div>
        </div>
      </section>

      {/* === 6. PHILOSOPHY === */}
      <section className="py-16 sm:py-20 relative" data-testid="about-philosophy">
        <div className="container-custom max-w-3xl">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/80 font-semibold mb-3">{isHi ? 'दृष्टिकोण' : 'Philosophy'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-main mb-3">
              {isHi ? <>मेरा <span className="text-gold-gradient">दृष्टिकोण</span></> : <>My <span className="text-gold-gradient">approach</span></>}
            </h2>
            <div className="divine-divider mt-4 mb-2 mx-auto max-w-xs" />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                icon: ShieldCheck,
                hi: 'ज्योतिष भय नहीं देता',
                en: 'Jyotish never creates fear',
                bodyHi: 'सही मार्गदर्शन साहस देता है — डर नहीं। हर session शांति में समाप्त होना चाहिए, चिंता में नहीं।',
                bodyEn: 'Right guidance offers courage, not anxiety. Every session should end in calm, not in worry.',
              },
              {
                icon: Compass,
                hi: 'निर्णय आपका, स्पष्टता मेरी',
                en: 'Your decision, my clarity',
                bodyHi: 'मैं विकल्प नहीं थोपता — मैं आपकी ही कुंडली का दर्पण आपके सामने रखता हूँ।',
                bodyEn: 'I never impose answers — I simply hold up your chart as a mirror so you may see clearly.',
              },
              {
                icon: Sun,
                hi: 'कर्म और जागरूकता',
                en: 'Karma & awareness',
                bodyHi: 'ग्रह संकेत हैं, कर्म आत्मा का है। जागरूकता ही असली उपाय है।',
                bodyEn: 'Planets are indicators; karma belongs to the soul. Awareness itself is the deepest remedy.',
              },
              {
                icon: Flower2,
                hi: 'व्यावहारिक उपाय',
                en: 'Practical remedies',
                bodyHi: 'कोई महंगा ताबीज़ नहीं — मंत्र, दान, सेवा, अनुशासन, मेडिटेशन — जो आप घर पर कर सकें।',
                bodyEn: 'No expensive amulets — only mantra, daana, seva, discipline and meditation that you can practise at home.',
              },
            ].map((p) => {
              const Icon = p.icon;
              return (
                <div key={p.en} className="relative rounded-2xl border border-gold/15 bg-white/[0.025] p-5 sm:p-6">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-700/30 to-purple-900/40 border border-gold/30 flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5 text-gold" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg text-text-main">{isHi ? p.hi : p.en}</h3>
                      <p className="text-text-muted text-sm mt-1.5 leading-relaxed">{isHi ? p.bodyHi : p.bodyEn}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* === 7. MEDIA & PUBLIC PRESENCE === */}
      <section className="py-16 sm:py-20 relative bg-white/[0.015]" data-testid="about-media">
        <div className="container-custom max-w-4xl">
          <div className="text-center mb-10">
            <p className="text-[11px] uppercase tracking-[0.3em] text-gold/80 font-semibold mb-3">{isHi ? 'सार्वजनिक उपस्थिति' : 'Public Presence'}</p>
            <h2 className="font-heading text-3xl sm:text-4xl text-text-main mb-3">
              {isHi ? <>शास्त्र को आम जन तक <span className="text-gold-gradient">पहुँचाने का प्रयास</span></> : <>Bringing shastra <span className="text-gold-gradient">closer to people</span></>}
            </h2>
          </div>

          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-2xl border border-gold/15 bg-white/[0.03] p-5 text-center">
              <Tv className="w-7 h-7 text-gold mx-auto mb-3" />
              <h3 className="font-heading text-lg text-text-main">{isHi ? 'टीवी संवाद' : 'TV Discussions'}</h3>
              <p className="text-text-muted text-sm mt-1.5 leading-relaxed">{isHi ? 'विभिन्न ज्योतिष विषयों पर सार्वजनिक चर्चा।' : 'Public discussions on various Jyotish topics.'}</p>
            </div>
            <div className="rounded-2xl border border-gold/15 bg-white/[0.03] p-5 text-center">
              <Youtube className="w-7 h-7 text-gold mx-auto mb-3" />
              <h3 className="font-heading text-lg text-text-main">{isHi ? 'YouTube उपस्थिति' : 'YouTube Presence'}</h3>
              <p className="text-text-muted text-sm mt-1.5 leading-relaxed">{isHi ? 'शास्त्रीय व्याख्यान और दैनिक मार्गदर्शन।' : 'Shastric talks and daily guidance.'}</p>
            </div>
            <div className="rounded-2xl border border-gold/15 bg-white/[0.03] p-5 text-center">
              <Mic className="w-7 h-7 text-gold mx-auto mb-3" />
              <h3 className="font-heading text-lg text-text-main">{isHi ? 'आध्यात्मिक संवाद' : 'Spiritual Talks'}</h3>
              <p className="text-text-muted text-sm mt-1.5 leading-relaxed">{isHi ? 'मंदिरों, सत्संगों और सेमिनारों में।' : 'At temples, satsangs and seminars.'}</p>
            </div>
          </div>
        </div>
      </section>

      {/* === 8. CONSULTATION CTA === */}
      <section className="py-20 sm:py-24 relative overflow-hidden" data-testid="about-consult-cta">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-950 via-purple-900/80 to-amber-950/40" />
        <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-gold/10 blur-[140px]" />
        <div className="absolute -bottom-32 -left-32 w-[450px] h-[450px] rounded-full bg-purple/30 blur-[140px]" />

        <div className="container-custom max-w-3xl relative">
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-gold/40 to-amber-500/30 border-2 border-gold/40 mb-5 shadow-xl">
              <span className="text-2xl">🕉️</span>
            </div>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl text-text-main leading-tight mb-4">
              {isHi
                ? <>अगर भीतर से कोई प्रश्न <br className="hidden sm:block" /><span className="text-gold-gradient">परेशान कर रहा है…</span></>
                : <>If a question is quietly <br className="hidden sm:block" /><span className="text-gold-gradient">troubling you…</span></>}
            </h2>
            <p className="text-text-main text-lg leading-[1.85] max-w-xl mx-auto">
              {isHi
                ? 'तो एक बार बैठकर शांतिपूर्वक बात की जा सकती है। बिना जल्दी, बिना दबाव, बिना किसी automated report के।'
                : 'Then let us simply sit and speak — calmly, without rush, without pressure, without any automated report in between.'}
            </p>
          </div>

          <div className="rounded-3xl border-2 border-gold/40 bg-purple-950/50 backdrop-blur p-6 sm:p-8">
            <div className="grid sm:grid-cols-3 gap-5 mb-7">
              <div className="text-center">
                <div className="text-2xl font-heading text-gold mb-1">45–60</div>
                <div className="text-xs text-text-muted uppercase tracking-wider">{isHi ? 'मिनट' : 'Minutes'}</div>
                <div className="text-xs text-text-muted mt-1">{isHi ? 'सीधे संवाद' : 'Direct conversation'}</div>
              </div>
              <div className="text-center sm:border-x sm:border-gold/15">
                <div className="text-2xl font-heading text-gold mb-1">100%</div>
                <div className="text-xs text-text-muted uppercase tracking-wider">{isHi ? 'व्यक्तिगत' : 'Personalised'}</div>
                <div className="text-xs text-text-muted mt-1">{isHi ? 'आपकी कुंडली पर' : 'Around your chart'}</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-heading text-gold mb-1">{isHi ? '0' : 'No'}</div>
                <div className="text-xs text-text-muted uppercase tracking-wider">{isHi ? 'AI Report' : 'Automated Report'}</div>
                <div className="text-xs text-text-muted mt-1">{isHi ? 'सिर्फ शास्त्र' : 'Only shastra'}</div>
              </div>
            </div>

            <div className="text-center">
              <div className="text-[10px] uppercase tracking-[0.25em] text-gold/80 font-bold mb-1">{isHi ? 'व्यक्तिगत परामर्श — दक्षिणा' : 'Personal Consultation Dakshina'}</div>
              <div className="text-3xl sm:text-4xl font-heading text-gold mb-5">₹5,100</div>

              <a
                href={buildWA(isHi ? 'नमस्ते आचार्य जी, मैं व्यक्तिगत परामर्श बुक करना चाहता/चाहती हूँ।' : 'Namaste Acharya ji, I would like to book a personal consultation.')}
                target="_blank" rel="noopener noreferrer"
                onClick={() => onCTAClick('about_main_cta')}
                className="inline-flex items-center gap-2.5 px-8 py-4 rounded-full bg-gradient-to-r from-gold via-amber-400 to-amber-500 text-purple-950 font-bold text-base sm:text-lg shadow-2xl hover:scale-[1.03] active:scale-[0.98] transition-transform"
                data-testid="about-main-wa-cta">
                <MessageCircle className="w-5 h-5" />
                {isHi ? 'WhatsApp पर बात शुरू करें' : 'Start a conversation on WhatsApp'}
              </a>
              <p className="text-xs text-text-muted mt-4">
                {isHi ? '— आचार्य विवेक मुदगल स्वयं प्रत्युत्तर देंगे' : '— Acharya Vivek Mudgal will personally respond'}
              </p>
            </div>
          </div>

          <div className="mt-10 text-center">
            <Link to="/faq" className="inline-flex items-center gap-1.5 text-sm text-gold/80 hover:text-gold underline-offset-4 hover:underline">
              {isHi ? 'पहले अक्सर पूछे गए प्रश्न पढ़ें' : 'Read frequently asked questions first'} <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>

      {/* === 12. DISCLAIMER === */}
      <section className="py-10 border-t border-gold/10 bg-white/[0.01]" data-testid="about-disclaimer">
        <div className="container-custom max-w-3xl">
          <div className="flex items-start gap-3 text-center sm:text-left flex-col sm:flex-row">
            <ShieldCheck className="w-5 h-5 text-gold/60 shrink-0 mt-0.5" />
            <p className="text-text-muted/80 text-xs sm:text-sm leading-[1.85] italic">
              {isHi
                ? 'इस मंच पर दी जाने वाली मार्गदर्शन परंपरागत वैदिक ज्ञान पर आधारित है, और केवल आध्यात्मिक तथा व्यक्तिगत समझ के उद्देश्य से है। यह किसी प्रकार की चिकित्सीय, कानूनी या वित्तीय सलाह का स्थानापन्न नहीं है।'
                : 'The guidance shared on this platform is rooted in traditional Vedic knowledge and is intended for spiritual and personal insight purposes only. It is not a substitute for medical, legal or financial advice.'}
            </p>
          </div>
        </div>
      </section>
      <FloatingActions />

      <RelatedTools />
      <Footer />
    </div>
  );
}
